import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Info, X } from 'lucide-react';
import { Button } from '@scio3/ui';
import { formatDateLong, formatWithPattern } from '@/lib/format-date';
import { useDunningAdvisory } from '@/hooks/useDunningAdvisory';
import { useDunningContactLine } from '@/hooks/useDunningContactLine';

/**
 * Pre-emptive advisory banner — CC day −30 (card expiring) / day −7 (upcoming
 * charge), PO day −7 (upcoming invoice).
 *
 * Deliberately gentler than `DunningBanner`:
 * - `variant="warning"`, never `destructive` — nothing has gone wrong yet.
 * - It has an **X**. The past-due banner does not, because that debt is real until
 *   paid; this is a heads-up and the customer is entitled to close it.
 * - Never a modal. Interrupting someone over a card that expires in 30 days is the
 *   line between a helpful nudge and a dark pattern.
 *
 * The advisory's dismissal IS recorded server-side (and advances the server-side tri-state), so a closed
 * banner returns once when the date is genuinely near, then stays closed.
 */
export function DunningAdvisoryBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showAdvisory, advisory, step, daysRemaining, acknowledge, payPath, canPay } =
    useDunningAdvisory();
  const contactLine = useDunningContactLine();

  const handleAction = useCallback(() => {
    acknowledge('paid');
    navigate(payPath);
  }, [acknowledge, navigate, payPath]);

  const handleDismiss = useCallback(() => acknowledge('dismissed'), [acknowledge]);

  if (!showAdvisory || !advisory) return null;

  // A card's expiry is a CALENDAR date: staff-app stores it as UTC midnight of the
  // month's last day (`Date.UTC(exp_year, exp_month, 0)`), so it is formatted in UTC or a
  // US viewer would read the day before. The other rungs carry real Stripe instants
  // (`current_period_end`, `next_payment_attempt`), which read correctly in local time.
  const dateText = !advisory.advisoryAt
    ? ''
    : step === 'cardExpiry'
      ? formatWithPattern(advisory.advisoryAt, 'MMMM D, YYYY', '', { tz: 'UTC' })
      : formatDateLong(advisory.advisoryAt);
  // "in {{count}} days" only makes sense a day or more out. On the day itself
  // Math.ceil gives 0 — "in 0 days" — so the dated copy is used only from 1 up,
  // and the count drives i18next's plural (`_one` / `_other`): "in 1 day".
  const days = daysRemaining ?? 0;
  const countable = !!dateText && days >= 1;
  const last4 = advisory.cardLast4?.trim();

  /**
   * Every variant names a real date. Falls back to a dateless form rather than
   * printing an empty or invalid one.
   */
  const message = (() => {
    if (step === 'cardExpiry') {
      // Without the last four digits "the card ending  expires…" reads broken, so
      // name the card only when we actually know it.
      if (!dateText) return t('dunning.advisory.cardExpiry');
      return last4
        ? t('dunning.advisory.cardExpiryDated', { date: dateText, last4 })
        : t('dunning.advisory.cardExpiryDatedNoCard', { date: dateText });
    }
    // The day −45 annual rungs reuse the week-out copy: it is dated and counts
    // the days, so it reads correctly at either lead.
    if (step === 'upcomingInvoice' || step === 'annualRenewalInvoice') {
      return countable
        ? t('dunning.advisory.upcomingInvoiceDated', { date: dateText, count: days })
        : t('dunning.advisory.upcomingInvoice');
    }
    return countable
      ? t('dunning.advisory.upcomingChargeDated', { date: dateText, count: days })
      : t('dunning.advisory.upcomingCharge');
  })();

  // Bold the date within the message, like DunningBanner's shutoff date, without
  // splitting the translated string into fragments: find the interpolated date
  // and wrap just that.
  const messageNode =
    dateText && message.includes(dateText) ? (
      <>
        {message.slice(0, message.indexOf(dateText))}
        <strong className="font-bold">{dateText}</strong>
        {message.slice(message.indexOf(dateText) + dateText.length)}
      </>
    ) : (
      message
    );

  const ctaLabel =
    step === 'cardExpiry' ? t('dunning.advisory.cta.updateCard') : t('dunning.advisory.cta.review');

  /**
   * Same full-bleed geometry as `DunningBanner` and the trial banner: `w-full`,
   * padding inside the bar, content centred. The old inset `Alert` card left
   * uneven margins on every edge and pinned the CTA to the far right.
   *
   * Amber rather than red, always. Nothing is late yet — this is a heads-up about
   * a card that is going to expire or an invoice about to fall due, and dressing
   * it in the same colour as a real past-due notice would spend the alarm early.
   */
  return (
    <div
      className="relative flex min-h-12 w-full items-center justify-center gap-4 bg-dunning-advisory py-2 pr-12 pl-6 text-sm text-dunning-advisory-foreground"
      role="status"
      data-testid="dunning-advisory-banner"
    >
      <span className="flex items-start gap-2 sm:items-center">
        <Info className="mt-0.5 h-5 w-5 shrink-0 sm:mt-0" aria-hidden="true" />
        <span>{messageNode}</span>
      </span>
      {/* A viewer who cannot open the destination gets who to ask instead of a
          button that lands on "no access" (see canOpenPayPath). */}
      {canPay ? (
        <Button
          size="sm"
          variant="outline"
          onClick={handleAction}
          data-testid="dunning-advisory-cta"
          className="h-8 shrink-0 px-5 text-xs font-semibold"
        >
          {ctaLabel}
        </Button>
      ) : (
        contactLine && <span data-testid="dunning-advisory-contact-line">{contactLine}</span>
      )}
      {/* Equal-weight dismiss, per the design note: an advisory the customer
          cannot wave away is a dark pattern, and nothing is even late yet.
          Pinned to the bar's right edge like PromotionalBanner's close button,
          so every dismissible banner on scio3 closes from the same spot. */}
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={handleDismiss}
        aria-label={t('dunning.advisory.dismiss')}
        data-testid="dunning-advisory-dismiss-btn"
        className="absolute right-3 shrink-0 rounded-full transition-colors hover:bg-black/10 dark:hover:bg-white/10"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
